import { useState, type ReactNode } from "react";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface InlineCreateProps {
  label: string;
  placeholder: string;
  icon: ReactNode;
  busy: boolean;
  classes?: {
    trigger?: string;
    form?: string;
    input?: string;
  };
  onCreate: (name: string) => Promise<boolean>;
  children: (expanded: boolean, trigger: ReactNode) => ReactNode;
}

/** A "+ New …" button that swaps itself for a name field. */
export function InlineCreate({
  label,
  placeholder,
  icon,
  busy,
  classes,
  onCreate,
  children,
}: InlineCreateProps) {
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState("");

  const close = () => {
    setExpanded(false);
    setName("");
  };

  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    if (await onCreate(trimmed)) close();
  };

  const trigger = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() => setExpanded(true)}
      className={classes?.trigger}
    >
      {icon}
      {label}
    </Button>
  );

  return (
    <>
      {children(expanded, expanded ? null : trigger)}
      {expanded && (
        <div className={classes?.form}>
          <Input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void submit();
              } else if (event.key === "Escape") {
                close();
              }
            }}
            placeholder={placeholder}
            className={classes?.input}
            disabled={busy}
          />
          <Button
            type="button"
            size="sm"
            onClick={() => void submit()}
            disabled={!name.trim() || busy}
            className="h-9 gap-1 text-xs"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Create
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={close}
            disabled={busy}
            className="h-9 w-9 p-0"
            aria-label="Cancel"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </>
  );
}
